/**
 * Choosing a course, on the screens that are about people rather than about one course.
 *
 * THIS REPLACES `CourseToolPage`, which gated Grades, Progress and Certificates behind
 * "choose a course…" and therefore opened on an empty page — in an academy with a hundred
 * courses and learners in a dozen of them, the administrator's first act was to guess. The
 * screens now open on the whole academy and the course is a FILTER, the same way Enrolments
 * treats it, with the drill-in kept for the things that genuinely belong to one course: a
 * gradebook's per-assignment columns, a certificate re-run, a grade report.
 *
 * Three pieces:
 *   `useCourseParam` keeps the choice in the query string, so "grades for this course" stays a
 *     link somebody can send — the one thing the old page got right.
 *   `CourseSwitcher` is the control. It is the prototype's Studio course switcher rather than a
 *     `<select>`, for a reason a dropdown cannot meet: each course needs a SECOND line — the
 *     count of whatever the screen is about — and an `<option>` is one line of plain text.
 *   `CourseCell` renders a course inside a table row, so one reads the same everywhere.
 *
 * WHAT EACH SCREEN OFFERS IS THE SCREEN'S OWN BUSINESS, and is passed in. "Courses with
 * learners" is right for Progress, where somebody at 0% is exactly the finding, and too loose
 * for the other two: a course with fifty learners and no certificates is a dead end on the
 * certificates screen, and a course that grades nothing is a dead end on grades. Each screen
 * derives its list from the fan-out it has already done, and says what it is leaving out.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '../shared/ui';
import type { AcademyCourse } from '../shared/lib/academy';

/** The chosen course key, and how to change it, held in the query string. */
export function useCourseParam(): [string, (courseKey: string) => void] {
  const [params, setParams] = useSearchParams();
  return [
    params.get('course') ?? '',
    (courseKey: string) => {
      // `replace` so picking one course after another does not fill up the Back button.
      setParams(courseKey ? { course: courseKey } : {}, { replace: true });
    },
  ];
}

/** One course as the switcher shows it: a title, its identifiers, and the screen's own measure. */
export interface CourseChoice {
  courseKey: string;
  title: string;
  /** `number · run`, which is how these courses are told apart when two share a name. */
  code: string;
  /** What this screen counts, e.g. "3 certificates" or "12 learners". */
  measure: string;
}

/**
 * Build the choices for a set of courses, keeping only those the screen has something for.
 *
 * `keep` returns the measure to show, or null for "leave this one out". A course named in the
 * URL is kept whatever it says, so a link somebody sent still resolves rather than silently
 * snapping back to "all courses".
 */
export function courseChoices(
  courses: AcademyCourse[],
  keep: (course: AcademyCourse) => string | null,
  named: string,
): CourseChoice[] {
  const choices: CourseChoice[] = [];
  for (const entry of courses) {
    const measure = keep(entry);
    if (measure === null && entry.course_key !== named) continue;
    choices.push({
      courseKey: entry.course_key,
      title: entry.display_name,
      code: `${entry.number} · ${entry.run}`,
      measure: measure ?? 'nothing here yet',
    });
  }
  return choices;
}

/**
 * The course switcher: a button that names the current choice, and a searchable popover.
 *
 * Keyboard behaviour is the combobox one, because that is what this is: the search box keeps
 * focus while the arrows move a highlighted option, `aria-activedescendant` tells a screen
 * reader which one that is, Enter takes it, and Escape closes the menu and puts focus back on
 * the button — a popover that swallows focus is a popover somebody gets stuck in.
 */
export function CourseSwitcher({
  choices,
  value,
  onChange,
  allTitle = 'All courses',
  allMeasure,
  hiddenNote,
  disabled = false,
  searchPlaceholder = 'Search courses…',
}: {
  choices: CourseChoice[];
  value: string;
  onChange: (courseKey: string) => void;
  allTitle?: string;
  /** The sub-line for "everything", e.g. "10 courses with certificates". */
  allMeasure: string;
  /** What is not on the list and why, said out loud rather than silently omitted. */
  hiddenNote?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);

  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  /** "Everything" is an option in the list, not a separate control beside it. */
  const options: CourseChoice[] = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const everything: CourseChoice = { courseKey: '', title: allTitle, code: '', measure: allMeasure };
    const matched = needle
      ? choices.filter(
          (choice) =>
            choice.title.toLowerCase().includes(needle) ||
            choice.code.toLowerCase().includes(needle) ||
            choice.courseKey.toLowerCase().includes(needle),
        )
      : choices;
    return needle ? matched : [everything, ...matched];
  }, [choices, term, allTitle, allMeasure]);

  const current = choices.find((choice) => choice.courseKey === value);

  const close = (returnFocus = true) => {
    setOpen(false);
    setTerm('');
    if (returnFocus) button.current?.focus();
  };

  const choose = (courseKey: string) => {
    onChange(courseKey);
    close();
  };

  // Anywhere outside closes it. `mousedown` rather than `click` so the menu is gone before a
  // click lands on whatever is underneath.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) search.current?.focus();
  }, [open]);

  // Keep the highlighted option in view: with a hundred courses the arrow keys otherwise walk
  // off the bottom of the scrolling list and appear to do nothing.
  useEffect(() => {
    if (!open) return;
    const node = list.current?.children[active];
    if (node instanceof HTMLElement) node.scrollIntoView({ block: 'nearest' });
  }, [active, open, options.length]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (options.length === 0 ? 0 : (index + 1) % options.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (options.length === 0 ? 0 : (index - 1 + options.length) % options.length));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(0, options.length - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = options[active];
      if (picked) choose(picked.courseKey);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      // Tab means "done here" — leave rather than trapping, but do not leave a menu hanging
      // over the page.
      close(false);
    }
  };

  return (
    <div className={`course-switcher${open ? ' is-open' : ''}`} ref={root}>
      <button
        type="button"
        className="course-switcher__btn"
        ref={button}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by course"
        onClick={() => {
          setActive(0);
          setOpen((was) => !was);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setActive(0);
            setOpen(true);
          }
        }}
      >
        <Icon name="school" />
        <span className="course-switcher__btn-text">
          {current ? current.title : allTitle}
          <small className="course-switcher__btn-code">
            {current ? `${current.code} · ${current.measure}` : allMeasure}
          </small>
        </span>
        <Icon name="unfold_more" />
      </button>

      {open && (
        <div className="course-switcher__menu">
          <div className="course-switcher__search">
            <Icon name="search" />
            <input
              ref={search}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={options[active] ? `${listId}-${active}` : undefined}
              placeholder={searchPlaceholder}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
            />
          </div>

          <div className="course-switcher__list" id={listId} role="listbox" ref={list}>
            {options.map((option, index) => (
              <div
                key={option.courseKey || 'all'}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.courseKey === value}
                className={[
                  'course-switcher__item',
                  option.courseKey === value ? 'is-current' : '',
                  index === active ? 'is-active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                // The pointer picks on mouse DOWN to match the outside-click listener above,
                // which would otherwise close the menu before a click could land.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option.courseKey);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="course-switcher__thumb">
                  <Icon name={option.courseKey ? 'school' : 'dashboard' } />
                </span>
                <span className="course-switcher__text">
                  <strong>{option.title}</strong>
                  <span className="course-switcher__meta">
                    {option.code ? <span>{option.code}</span> : null}
                    <span>{option.measure}</span>
                  </span>
                </span>
                {option.courseKey === value && <Icon name="check" />}
              </div>
            ))}

            {options.length === 0 && (
              <p className="course-switcher__empty">No course matches that.</p>
            )}
          </div>

          {hiddenNote && <p className="course-switcher__foot">{hiddenNote}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * The course's name for a table cell, with its number and run underneath.
 *
 * Shared by all three screens so a course reads identically wherever it appears, and so an
 * unknown key degrades the same way everywhere: to the key itself, in mono, rather than to a
 * blank cell that looks like missing data.
 */
export function CourseCell({ course, courseId }: { course?: AcademyCourse; courseId: string }) {
  if (!course) return <span className="mono">{courseId}</span>;
  return (
    <>
      {course.display_name}
      <small className="admin-sub">
        {course.number} · {course.run}
      </small>
    </>
  );
}
